// src/components/invoices/billPainterHtml.js

function n2(v) {
  const x = Number(String(v ?? "0").replace(/[,₹\s]/g, ""));
  if (!isFinite(x)) return "0.00";
  return x.toFixed(2);
}
function toNum(v) {
  const x = Number(String(v ?? "0").replace(/[,₹\s]/g, ""));
  return isFinite(x) ? x : 0;
}
function safe(s) {
  return String(s ?? "").trim();
}
function isIso(s) {
  return /^\d{4}-\d{2}-\d{2}/.test(String(s ?? ""));
}
function ensureIso(s) {
  const t = String(s ?? "").trim();
  if (isIso(t)) return t.slice(0, 10);
  // dd/MM/yyyy -> yyyy-MM-dd
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(t)) {
    const [dd, mm, yy] = t.split("/");
    return `${yy}-${mm}-${dd}`;
  }
  return t;
}
function ddmmyyyy(iso) {
  try {
    const x = ensureIso(iso);
    const [y, m, d] = x.split("-");
    return `${d}/${m}/${y}`;
  } catch {
    return String(iso ?? "");
  }
}
function onlyDay(iso) {
  try {
    const x = ensureIso(iso);
    const d = x.split("-")[2];
    return String(d ?? "").padStart(2, "0");
  } catch {
    return String(iso ?? "");
  }
}
function pickArray(root, ...keys) {
  for (const k of keys) {
    const v = root?.[k];
    if (Array.isArray(v)) return v;
    if (v && typeof v === "object") {
      if (Array.isArray(v.rows)) return v.rows;
      if (Array.isArray(v.data)) return v.data;
      if (Array.isArray(v.items)) return v.items;
    }
  }
  return null;
}
function isEvening(timeStr) {
  const v = String(timeStr ?? "").trim().toLowerCase();
  return v.includes("even") || v.includes("sand") || v === "e";
}
function abbrevType(typeStr) {
  const v = String(typeStr ?? "").trim().toLowerCase();
  if (v === "b" || v.includes("buff")) return "B";
  if (v === "c" || v.includes("cow")) return "C";
  // if backend already sends "B"/"C"
  if (String(typeStr ?? "").trim().length <= 3) return String(typeStr ?? "").trim();
  return "";
}
function typeRank(code) {
  const v = String(code ?? "").trim().toUpperCase();
  if (v === "B") return 0;
  if (v === "C") return 1;
  return 2;
}

export function billPainterCss() {
  // A4 portrait base bill
  return `
  :root{ --b:#000; --g:#bfbfbf; --gg:#8a8a8a; }
  *{ box-sizing:border-box; }
  .bpWrap{ width:100%; }
  .bpBill{
    width:210mm; min-height:297mm;
    margin:0 auto;
    padding:12mm 10mm;
    font-family: Arial, Helvetica, sans-serif;
    color:var(--b);
    background:#fff;
  }
  .bpCenter{ text-align:center; }
  .bpTitle{ font-size:18px; font-weight:700; line-height:1.2; }
  .bpSub{ font-size:12px; margin-top:2px; }
  .bpRow{ display:flex; justify-content:space-between; gap:10px; margin-top:6px; font-size:12px; }
  .bpHr{ height:1px; background:#999; margin:8px 0; }
  .bpRight{ text-align:right; white-space:nowrap; }
  .bpBold{ font-weight:700; }

  table.bpT{ width:100%; border-collapse:collapse; font-size:12px; }
  .bpT th,.bpT td{ border:1px solid #000; padding:4px 6px; }
  .bpT th{ background:#e9e9e9; font-weight:700; }
  .bpT .r{ text-align:right; }
  .bpT .c{ text-align:center; }
  .bpT .l{ text-align:left; }
  .bpBand th{
    background:#fff;
    border-left:1px solid #000;
    border-right:1px solid #000;
    border-top:1px solid #000;
    border-bottom:1px solid #000;
    font-weight:700;
  }
  .bpSectionTitle{
    margin-top:10px;
    font-size:12px;
    font-weight:700;
    background:#e9e9e9;
    border:1px solid #000;
    padding:6px;
  }
  .bpBottomStrip{
    margin-top:10px;
    border-top:1px solid #000;
    border-bottom:1px solid #000;
    padding:6px 0;
    font-size:12px;
    display:flex;
    justify-content:space-between;
    gap:10px;
    flex-wrap:wrap;
  }

  /* ---- print helpers ---- */
  @page { size: A4; margin: 0; }
  @media print{
    body{ -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .bpBill{ margin:0; }
  }

  /* ---- 2-up landscape page (like MdbillprintMulti) ---- */
  @page bpLandscape { size: A4 landscape; margin: 8mm; }
  .bpPages{ width:100%; }
  .bpSheet{
    page: bpLandscape;
    width:100%;
    display:flex;
    gap:8mm;
    align-items:flex-start;
  }
  .bpSlot{
    flex:1;
    border:0;
  }
  /* scale a portrait bill into half landscape */
  .bpSlot .bpBill{
    width: calc( (297mm - 8mm - 8mm) / 2 );
    min-height:auto;
    padding:8mm 7mm;
  }
  `;
}

export function buildBillPainterHtml(payloadJson, centerInfo = {}) {
  let root;
  try {
    root = typeof payloadJson === "string" ? JSON.parse(payloadJson) : payloadJson;
  } catch {
    root = {};
  }

  const hdr = root?.header || {};
  const cname = safe(hdr.cname);
  const kno = safe(hdr.kno);
  const d1 = ensureIso(hdr.date1);
  const d2 = ensureIso(hdr.date2);

  const centerName = safe(centerInfo.centerName || centerInfo.kendraname || centerInfo.name);
  const city = safe(centerInfo.city);
  const tal = safe(centerInfo.tal);
  const dist = safe(centerInfo.dist);

  const owner = safe(centerInfo.owner);
  const mobile = safe(centerInfo.mobno || centerInfo.mobile);

  const sankRows = pickArray(root, "sanklan", "sank", "milk") || [];
  const kap = root?.kapat || {};
  const kapRows = Array.isArray(kap.rows) ? kap.rows : [];
  const kapTot = kap.totals || {};
  const deyRows = pickArray(root, "dey", "additions") || [];
  const summary = root?.summary || {};

  const gross = toNum(summary.gross);
  const kapatCut = toNum(summary.kapat);
  const payable = toNum(summary.payable);
  const prevBal = toNum(summary.balance);
  const overallBal = toNum(kapTot.balance);

  // --- group like Android: day -> type -> [morning,eve]
  const dayMap = new Map();
  for (const r of sankRows) {
    if (!r) continue;
    const day = onlyDay(r.date);
    const milkCode = abbrevType(r.type);
    const idx = isEvening(r.time ?? r.vibhag) ? 1 : 0;

    if (!dayMap.has(day)) dayMap.set(day, new Map());
    const typeMap = dayMap.get(day);

    if (!typeMap.has(milkCode)) typeMap.set(milkCode, [null, null]);
    typeMap.get(milkCode)[idx] = r;
  }

  // totals like Android
  const sumL = [0, 0];
  const sumAmt = [0, 0];
  const numFat = [0, 0];
  const numSnf = [0, 0];

  function rowCells(rr) {
    if (!rr) return ["", "", "", "", ""];
    const L = toNum(rr.liter);
    const fat = toNum(rr.fat);
    const snf = toNum(rr.snf);
    const rate = toNum(rr.rate);
    const amt = toNum(rr.amt);

    return [n2(L), n2(fat), n2(snf), n2(rate), n2(amt)];
  }

  // build sanklan rows html (B first then C)
  let sankHtml = "";
  const days = Array.from(dayMap.keys()); // keep insertion order like Android map
  for (const day of days) {
    const typeMap = dayMap.get(day);
    const codes = Array.from(typeMap.keys()).sort((a, b) => {
      const ra = typeRank(a);
      const rb = typeRank(b);
      if (ra !== rb) return ra - rb;
      return String(a).localeCompare(String(b));
    });

    for (const code of codes) {
      const pair = typeMap.get(code) || [null, null];
      const morn = pair[0];
      const eve = pair[1];

      const m = rowCells(morn);
      const e = rowCells(eve);

      if (morn) {
        const L = toNum(m[0]);
        sumL[0] += L;
        sumAmt[0] += toNum(m[4]);
        numFat[0] += L * toNum(m[1]);
        numSnf[0] += L * toNum(m[2]);
      }
      if (eve) {
        const L = toNum(e[0]);
        sumL[1] += L;
        sumAmt[1] += toNum(e[4]);
        numFat[1] += L * toNum(e[1]);
        numSnf[1] += L * toNum(e[2]);
      }

      sankHtml += `
        <tr>
          <td class="c">${day}</td>
          <td class="c">${safe(code)}</td>
          <td class="r">${m[0]}</td><td class="r">${m[1]}</td><td class="r">${m[2]}</td><td class="r">${m[3]}</td><td class="r">${m[4]}</td>
          <td class="r">${e[0]}</td><td class="r">${e[1]}</td><td class="r">${e[2]}</td><td class="r">${e[3]}</td><td class="r">${e[4]}</td>
        </tr>
      `;
    }
  }

  const avgFat = [
    sumL[0] > 0 ? numFat[0] / sumL[0] : 0,
    sumL[1] > 0 ? numFat[1] / sumL[1] : 0,
  ];
  const avgSnf = [
    sumL[0] > 0 ? numSnf[0] / sumL[0] : 0,
    sumL[1] > 0 ? numSnf[1] / sumL[1] : 0,
  ];
  const avgRate = [
    sumL[0] > 0 ? sumAmt[0] / sumL[0] : 0,
    sumL[1] > 0 ? sumAmt[1] / sumL[1] : 0,
  ];

  const GL = sumL[0] + sumL[1];
  const GA = sumAmt[0] + sumAmt[1];
  const GF = GL > 0 ? (numFat[0] + numFat[1]) / GL : 0;
  const GS = GL > 0 ? (numSnf[0] + numSnf[1]) / GL : 0;
  const GR = GL > 0 ? GA / GL : 0;

  // kapat table
  let kapHtml = "";
  let totPrev = 0, totCut = 0, totBal = 0, totCr = 0;
  for (const r of kapRows) {
    const name = safe(r?.name);
    const prev = toNum(r?.prevpos);
    const cut = toNum(r?.cutnow);
    const bal = toNum(r?.balance);
    const cr = toNum(r?.cr);
    totPrev += prev; totCut += cut; totBal += bal; totCr += cr;

    kapHtml += `
      <tr>
        <td class="l">${name}</td>
        <td class="r">${n2(prev)}</td>
        <td class="r">${n2(cut)}</td>
        <td class="r">${n2(bal)}</td>
        <td class="r">${n2(cr)}</td>
      </tr>
    `;
  }
  kapHtml += `
    <tr>
      <td class="l bpBold">Total</td>
      <td class="r bpBold">${n2(totPrev)}</td>
      <td class="r bpBold">${n2(totCut)}</td>
      <td class="r bpBold">${n2(totBal)}</td>
      <td class="r bpBold">${n2(totCr)}</td>
    </tr>
  `;

  // additional payables
  let deyHtml = "";
  let deyTot = 0;
  for (const r of deyRows) {
    const nm = safe(r?.name);
    const amt = toNum(r?.amt);
    if (!amt) continue;
    deyTot += amt;
    deyHtml += `<tr><td class="l">${nm}</td><td class="r">${n2(amt)}</td></tr>`;
  }
  deyHtml += `<tr><td class="l bpBold">Total</td><td class="r bpBold">${n2(deyTot)}</td></tr>`;

  // final html
  const addressLine = [city, tal, dist].filter(Boolean).join(", ");

  const html = `
  <div class="bpWrap">
    <div class="bpBill">
      <div class="bpCenter">
        <div class="bpTitle">${centerName || "-"}</div>
        <div class="bpSub">${addressLine || ""}</div>
      </div>

      <div class="bpRow">
        <div class="bpBold">${safe(kno)} - ${safe(cname)}</div>
        <div class="bpRight bpBold">Date: ${ddmmyyyy(d1)} to ${ddmmyyyy(d2)}</div>
      </div>

      <div class="bpRow" style="margin-top:4px;">
        <div></div>
        <div class="bpRight">
          <div>Bill No : ${safe(kno)}</div>
          ${owner ? `<div>${owner}</div>` : ""}
          ${mobile ? `<div>${mobile}</div>` : ""}
        </div>
      </div>

      <div class="bpHr"></div>

      <table class="bpT">
        <thead>
          <tr class="bpBand">
            <th colspan="2"></th>
            <th colspan="5" class="c">Morning</th>
            <th colspan="5" class="c">Evening</th>
          </tr>
          <tr>
            <th class="c">Date</th>
            <th class="c">Milk</th>
            <th class="r">Liter</th><th class="r">Fat</th><th class="r">SNF</th><th class="r">RATE</th><th class="r">Amount</th>
            <th class="r">Liter</th><th class="r">Fat</th><th class="r">SNF</th><th class="r">RATE</th><th class="r">Amount</th>
          </tr>
        </thead>
        <tbody>
          ${sankHtml || ""}
          <tr>
            <td class="l bpBold">Total</td>
            <td></td>
            <td class="r bpBold">${n2(sumL[0])}</td>
            <td class="r bpBold">${n2(avgFat[0])}</td>
            <td class="r bpBold">${n2(avgSnf[0])}</td>
            <td class="r bpBold">${n2(avgRate[0])}</td>
            <td class="r bpBold">${n2(sumAmt[0])}</td>

            <td class="r bpBold">${n2(sumL[1])}</td>
            <td class="r bpBold">${n2(avgFat[1])}</td>
            <td class="r bpBold">${n2(avgSnf[1])}</td>
            <td class="r bpBold">${n2(avgRate[1])}</td>
            <td class="r bpBold">${n2(sumAmt[1])}</td>
          </tr>

          <tr>
            <td colspan="7" class="c bpBold">Total</td>
            <td class="r bpBold">${n2(GL)}</td>
            <td class="r bpBold">${n2(GF)}</td>
            <td class="r bpBold">${n2(GS)}</td>
            <td class="r bpBold">${n2(GR)}</td>
            <td class="r bpBold">${n2(GA)}</td>
          </tr>
        </tbody>
      </table>

      <div class="bpSectionTitle">Deduction Type</div>
      <table class="bpT">
        <thead>
          <tr>
            <th class="l">Deduction Type</th>
            <th class="r">Total balance</th>
            <th class="r">Deduction</th>
            <th class="r">Balance</th>
            <th class="r">Current Deposit</th>
          </tr>
        </thead>
        <tbody>
          ${kapHtml}
        </tbody>
      </table>

      <div class="bpSectionTitle">Additional Payables</div>
      <table class="bpT">
        <thead>
          <tr><th class="l">Additional Payables</th><th class="r">Amount</th></tr>
        </thead>
        <tbody>
          ${deyHtml}
        </tbody>
      </table>

      <div class="bpBottomStrip">
        <div class="bpBold">Previous: ${n2(prevBal)}</div>
        <div class="bpBold">Amount: ${n2(gross || GA)}</div>
        <div class="bpBold">Deduction: ${n2(kapatCut)}</div>
        <div class="bpBold">Payable: ${n2(payable)}</div>
        <div class="bpBold">Outstanding: ${n2(overallBal)}</div>
      </div>
    </div>
  </div>
  `;

  return html;
}
